// TODO: include third party cameras?

import React, { useState } from "react";
import BlogWrapper from "~components/blog-wrapper";
import { Section, SubSection } from "~components/text-helpers";
import Latex from "react-latex";
import { graphql, Link } from "gatsby";
import Img, { FixedObject, FluidObject } from "gatsby-image";
import lifecycle from "~media/lifecycle.svg";
import { css } from "@emotion/react";
import { UnityAPI } from "~components/api-data";
import { Emoji } from "emoji-mart";
import { Popover } from "antd";

import SEMANTIC_SCHOLAR_ICON from "~icons/semantic-scholar-thin.svg";
import { GithubOutlined, HomeFilled } from "@ant-design/icons";

import {
  Code,
  Response,
  Attribute,
  IL,
  APIMethod,
  VSpace,
  SideBySideCode,
  Warning,
  Remark,
} from "~components/documentation";
import color from "~styles/color";

function PositionAttribute(props: {
  data: any;
  type: string;
  hashPrefix: string;
  default?: string;
  required?: boolean;
}) {
  return (
    <Attribute
      hashPrefix={props.hashPrefix}
      name="position"
      type={props.type}
      default={props.default}
      required={props.required}
      description={
        <>
          <Latex>
            The global position of the agent, with keys for $$x$$, $$y$$, and
            $$z$$.
          </Latex>
          <VSpace s="15px" />
          If <IL>position</IL> is unspecified, the agent's position will remain
          unchanged.
          <VSpace s="15px" />
          <Latex>
            The coordinate space for each RoboTHOR scene is shown below. Notice
            that $$y$$ corresponds to the upward coordinate in 3D space.
          </Latex>
          <VSpace s="15px" />
          <Img fluid={props.data.dimensions.childImageSharp.fluid} />
        </>
      }
    />
  );
}

export default function Docs({ data, location }) {
  const [highlightedParam, setHighlightedParam] = useState("");
  return (
    <BlogWrapper
      title="ManipulaTHOR API Reference"
      subtitle={
        <div
          css={css`
            margin-top: 25px;
            margin-bottom: -20px;
            > a div {
              background-color: ${color.gray8 + "88"};
              color: ${color.gray4};
              border-radius: 5px;
              display: inline-block;
              padding-left: 10px;
              padding-right: 10px;
              padding-bottom: 4px;
              padding-top: 1px;
              margin-bottom: 10px;
              margin-top: 10px;
              margin-left: 8px;
              margin-right: 8px;
              font-size: 18px;
              transition-duration: 0.3s;

              &:hover {
                background-color: ${color.gray8 + "55"};
                color: ${color.gray6};
              }
            }
          `}
        >
          <a href="//github.com/allenai/manipulathor/" target="_blank">
            <div>
              <Popover
                placement="bottom"
                content={
                  <div>
                    The AllenAct codebase to reproduce
                    <br /> the models presented in the
                    <br /> canonical ManipulaTHOR paper.
                  </div>
                }
                title={
                  <div>
                    <GithubOutlined /> allenai/manipulathor
                  </div>
                }
              >
                <GithubOutlined /> Code
              </Popover>
            </div>
          </a>
          <Link to="/manipulathor">
            <div>
              <Popover
                placement="bottom"
                content={
                  <div>
                    Learn about ManipulaTHOR,
                    <br /> its high-level features,
                    <br /> and motivation!
                  </div>
                }
                title={
                  <div>
                    <HomeFilled /> /manipulathor
                  </div>
                }
              >
                <HomeFilled /> Homepage
              </Popover>
            </div>
          </Link>
          <a
            href="//api.semanticscholar.org/CorpusID:233346916"
            target="_blank"
          >
            <div>
              <Popover
                placement="bottom"
                content={
                  <div>
                    We present ManipulaTHOR, a framework
                    <br /> that facilitates visual manipulation of
                    <br /> objects using a robotic arm...
                  </div>
                }
                title={
                  <div>
                    <img
                      src={SEMANTIC_SCHOLAR_ICON}
                      css={css`
                        height: 15px;
                        margin-bottom: 4px;
                        filter: invert(1);
                      `}
                    />{" "}
                    Paper PDF
                  </div>
                }
              >
                <img
                  src={SEMANTIC_SCHOLAR_ICON}
                  css={css`
                    height: 15px;
                    margin-bottom: 4px;
                  `}
                />{" "}
                Paper
              </Popover>
            </div>
          </a>
        </div>
      }
      maxContentWidth="950px"
      keywords="ManipulaTHOR, API, Documentation, Python"
      description="Documentation for the ManipulaTHOR environment within AI2-THOR."
      metaImage="https://ai2thor.allenai.org/media/manipulathor/manipulathor-meta.jpg"
    >
      <Section sectionTitle="Installation" emoji="computer">
        <p>
          ManipulaTHOR comes packaged with AI2-THOR, which can be installed in
          Python using pip:
        </p>
        <Code language="bash">pip install ai2thor</Code>
        <p
          css={css`
            margin-top: 10px;
          `}
        >
          It is currently designed to work with iTHOR scenes. But, compared to
          iTHOR, the actions are significantly lower level by leveraging a
          robotic arm.
        </p>
      </Section>
      <Section sectionTitle="Initialization" emoji="star-struck">
        <APIMethod
          location={location}
          methodKey="initialization"
          parentAPI={UnityAPI.ArmAgent}
          setHighlightedParam={setHighlightedParam}
          highlightedParam={highlightedParam}
        />
      </Section>
      <Section sectionTitle="Reset" emoji="new_moon_with_face">
        <p>
          Any{" "}
          <Link to="#initialization">
            <IL>Controller</IL> Initialization
          </Link>{" "}
          parameter can later be changed by calling the reset method. For
          instance, we can call:
        </p>
        <Code>controller.reset(scene="FloorPlan15", fieldOfView=80)</Code>
        <p
          css={css`
            margin-top: 10px;
          `}
        >
          The values will default to what they were upon the most recent reset
          or initialization. For instance, if you initialized with{" "}
          <IL>fieldOfView=45</IL>, then called <IL>reset</IL> with{" "}
          <IL>fieldOfView=60</IL>, calling <IL>reset</IL> again, without passing
          in <IL>fieldOfView</IL> will keep <IL>fieldOfView=60</IL>.
        </p>
      </Section>
      <Section sectionTitle="Movement" emoji="owl">
        <p>
          The arm agent has three components that may move: the arm, which has
          several degrees of freedom, the arm base, and the agent's body.
        </p>
        <SubSection sectionTitle="Move Agent" emoji="elephant">
          <APIMethod
            location={location}
            methodKey="MoveAgent"
            parentAPI={UnityAPI.ArmAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
          />
        </SubSection>
        <SubSection sectionTitle="Rotate Agent" emoji="upside_down_face">
          <APIMethod
            location={location}
            methodKey="RotateAgent"
            parentAPI={UnityAPI.ArmAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
          />
        </SubSection>
        <SubSection sectionTitle="Move Arm" emoji="call_me_hand">
          <APIMethod
            location={location}
            methodKey="MoveArm"
            parentAPI={UnityAPI.ArmAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
            pageQuery={data}
          />
        </SubSection>
        <SubSection sectionTitle="Move Arm Base" emoji="man-tipping-hand">
          <APIMethod
            location={location}
            methodKey="MoveArmBase"
            parentAPI={UnityAPI.ArmAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
            pageQuery={data}
          />
        </SubSection>
        <SubSection sectionTitle="Camera Rotation" emoji="movie_camera">
          <p>
            We can adjust the camera's pitch by utilizing the <IL>LookUp</IL>{" "}
            and <IL>LookDown</IL> commands. Both increment in{" "}
            <Latex>$$30^\circ$$</Latex> intervals, with angles being clamped
            between <Latex>$$60^\circ$$</Latex> in the downward direction and{" "}
            <Latex>$$30^\circ$$</Latex> in the upward direction.
          </p>
          <Code
            lines={['controller.step("LookUp")', 'controller.step("LookDown")']}
          />
        </SubSection>
        <SubSection sectionTitle="Teleport" emoji="tophat">
          <APIMethod
            location={location}
            methodKey="Teleport"
            parentAPI={UnityAPI.PhysicsAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
          />
          <VSpace s="25px" />
          <APIMethod
            location={location}
            methodKey="TeleportFull"
            parentAPI={UnityAPI.PhysicsAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
          />
          <p>
            It is often useful to randomize the position of the agent in the
            scene, before starting an episode. Here, we can use:
          </p>
          <ol>
            <li>
              <Link to="/ithor/documentation/environment-state/#sub-get-reachable-positions">
                GetReachablePositions
              </Link>
              , which does an optimized BFS over a grid spaced out by the
              initialized <IL>gridSize</IL>. The valid positions are then added
              and returned in a list.
            </li>
            <li>
              <IL>Teleport</IL>, which can take a given position, and transform
              the agent to that position.
            </li>
          </ol>
          <p>The process is illustrated below:</p>
          <SideBySideCode
            example={
              <>
                <Code
                  lines={[
                    "positions = controller.step(",
                    '    action="GetReachablePositions"',
                    ').metadata["actionReturn"]',
                  ]}
                  caption="Step 1: Get the Positions"
                />
                <Response
                  lines={[
                    "[",
                    "    dict(x=(...), y=(...), z=(...)),",
                    "    dict(x=(...), y=(...), z=(...)),",
                    "    dict(x=(...), y=(...), z=(...)),",
                    "    {...}",
                    "    dict(x=(...), y=(...), z=(...)),",
                    "]",
                  ]}
                />
                <Code
                  caption="Step 2: Teleport to a Position"
                  lines={[
                    "import random",
                    "position = random.choice(positions)",
                    "controller.step(",
                    '    action="Teleport",',
                    "    position=position",
                    ")",
                  ]}
                />
              </>
            }
            argsTitle="Get Reachable Positions Response"
          >
            <Attribute
              hashPrefix="get-reachable-positions"
              name="actionReturn"
              type="list[dict[str, float]]"
              description={
                <>
                  <p>
                    A list of each position that the agent can be at, without
                    colliding into any other objects or going beyond the walls.
                  </p>
                  {/* <p>
                  <Latex>
                    The coordinate space for each RoboTHOR scene is shown below.
                    Notice that $$y$$ corresponds to the upward coordinate in 3D
                    space.
                  </Latex>
                </p> */}
                  {/* <VSpace s="15px" /> */}
                  {/* <Img fluid={data.dimensions.childImageSharp.fluid} /> */}
                </>
              }
            />
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Done" emoji="sleeping">
          <p>
            The <IL>Done</IL> action nothing to the state of the environment.
            But, it returns a cleaned up event with respect to the metadata.
          </p>
          <Code lines={['controller.step(action="Done")']} />
          <p
            css={css`
              margin-top: 10px;
            `}
          >
            It is often used in the definition of a successful navigation task
            (see{" "}
            <a href="//arxiv.org/pdf/1807.06757.pdf" target="_blank">
              Anderson et al.
            </a>
            ), where the agent must call the done action to signal that it knows
            that it's done, rather than arbitrarily or biasedly guessing.
          </p>
          <Warning>
            The <IL>Done</IL> action does literally nothing to the state of the
            environment. For instance, if the agent calls <IL>Done</IL> and then{" "}
            <IL>MoveAgent</IL>, the <IL>Done</IL> action will have no affect on
            preventing a <IL>MoveAgent</IL> action from executing.
          </Warning>
          <p>
            It is often used to return a cleaned up version of the metadata.
          </p>
        </SubSection>
      </Section>
      <Section sectionTitle="Interaction" emoji="construction_worker">
        <p>
          This section provides several actions that facilitate interaction with
          the ManipulaTHOR agent. If you have any additional actions that you
          would like to see, please{" "}
          <a href="https://github.com/allenai/ai2thor/issues" target="_blank">
            create an issue
          </a>{" "}
          and we'll see if we can support it!{" "}
        </p>
        <SubSection sectionTitle="Pickup Object" emoji="apple">
          <APIMethod
            location={location}
            methodKey="PickupObject"
            parentAPI={UnityAPI.ArmAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
            pageQuery={data}
          />
        </SubSection>
        <SubSection sectionTitle="Release Object" emoji="droplet">
          <SideBySideCode
            argsTitle="Release Object Description"
            example={
              <>
                <Code>controller.step(action="ReleaseObject")</Code>
                <Img
                  fluid={data.releaseObject.childImageSharp.fluid}
                  css={css`
                    margin-top: 15px;
                  `}
                />
              </>
            }
          >
            <p
              css={css`
                padding-left: 2%;
                padding-right: 2%;
                margin-top: 5px;
              `}
            >
              Drops any objects that the agent is currently holding.
            </p>
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Set Hand Sphere Radius" emoji="punch">
          <APIMethod
            location={location}
            methodKey="SetHandSphereRadius"
            parentAPI={UnityAPI.ArmAgent}
            setHighlightedParam={setHighlightedParam}
            highlightedParam={highlightedParam}
          />
        </SubSection>
      </Section>
      <Section sectionTitle="Environment State" emoji="earth_americas">
        <img src={lifecycle} />
        <p>
          The figure above shows AI2-THOR's agent-simulator interaction loop.
          Our backend scenes, actions, and observations are stored within Unity,
          a powerful real-time game engine.
        </p>
        <p>
          From the Python side, we sequentially interact with Unity by taking{" "}
          actions using AI2-THOR's Python <IL>Controller</IL>. We have already
          seen several actions, such as <IL>MoveAgent</IL>, <IL>RotateAgent</IL>
          , and <IL>LookUp</IL>.
        </p>
        <SubSection sectionTitle="Events" emoji="tada">
          <p>
            The returned data after executing any action is an <IL>Event</IL>.
            Each <IL>Event</IL> contains a ton of information to encode the
            state of the environment after the action has been executed:
          </p>
          <SideBySideCode
            example={
              <>
                <Code lines={["event = controller.step(...)"]} />
                <Code
                  caption={<>References the last executed Event</>}
                  lines={["controller.last_event"]}
                />
                <Response
                  lines={[
                    "<ai2thor.server.Event",
                    "    .metadata: {...}",
                    "",
                    "    .frame: {...}",
                    "    .cv2img: {...}",
                    "",
                    "    .depth_frame: {...}",
                    "",
                    "    .instance_segmentation_frame: {...}",
                    "    .instance_masks: {...}",
                    "    .instance_detections2D: {...}",
                    "",
                    // "    .semantic_segmentation_frame: {...}",
                    // "    .class_masks: {...}",
                    // "    .class_detections2D: {...}",
                    // "",
                    "    .color_to_object_id: {...}",
                    "    .object_id_to_color: {...}",
                    "/>",
                  ]}
                />
              </>
            }
            argsTitle="Event Response"
          >
            <Attribute
              hashPrefix="event"
              name="metadata"
              type="dict[str, any]"
              description={
                <p>
                  Contains information about the action and how it affected the
                  environment. See the <IL>Metadata</IL> section for more.
                </p>
              }
            />
            <Attribute
              hashPrefix="event"
              name="frame"
              type="numpy.ndarray"
              description={
                <p>
                  RGB frame of the scene from the agent egocentric perspective.
                  The size of the frame is the initialized (<IL>height</IL>,{" "}
                  <IL>width</IL>, 3) stored in the numpy.uint8 format.
                  {/* <VSpace s="15px" /> */}
                  {/* <Img fluid={data.rgb.childImageSharp.fluid} /> */}
                </p>
              }
            />
            <Attribute
              hashPrefix="event"
              name="cv2img"
              type="numpy.ndarray"
              description={
                <p>
                  Same as the <IL>frame</IL>, except image channels are in BGR
                  ordering. This is often useful with Python's OpenCV module
                  (i.e., cv2), which expects images with BGR orderings.
                  {/* <VSpace s="15px" />
                  <Img fluid={data.cv2.childImageSharp.fluid} /> */}
                </p>
              }
            />
            <Attribute
              hashPrefix="event"
              name="depth_frame"
              type="Optional[numpy.ndarray]"
              description={
                <>
                  <p>
                    Measures the distance, in meters, from the agent to each
                    pixel in the frame. The size of the frame is the initialized
                    (<IL>height</IL>, <IL>width</IL>) stored in the
                    numpy.float32 format.
                  </p>
                  {/* <VSpace s="15px" /> */}
                  {/* <Img fluid={data.depthFrame.childImageSharp.fluid} /> */}
                  {/* <VSpace s="15px" /> */}
                  <Remark>
                    <p>
                      Upon <IL>initialization</IL> or <IL>reset</IL>,{" "}
                      <IL>renderDepthImage</IL> must be set to <IL>True</IL> in
                      order for the depth frame to appear.
                    </p>
                  </Remark>
                </>
              }
            />
            <Attribute
              hashPrefix="event"
              name="instance_segmentation_frame"
              type="Optional[numpy.ndarray]"
              description={
                <>
                  <p>
                    Segments every object instance in the scene. The size of the
                    frame is (<IL>height</IL>, <IL>width</IL>, 3) stored in the
                    numpy.uint8 format. Each unique pixel color corresponds to a
                    different object, which are indexable with{" "}
                    <IL>color_to_object_id</IL> or <IL>object_id_to_color</IL>.
                  </p>
                  {/* <VSpace s="15px" />
                  <Img
                    fluid={data.instanceSegmentation.childImageSharp.fluid}
                  /> */}
                  {/* <VSpace s="15px" /> */}
                  <Remark backgroundColor={color.dark.green5}>
                    <p>
                      Upon <IL>initialization</IL> or <IL>reset</IL>,{" "}
                      <IL>renderInstanceSegmentation</IL> must be set to{" "}
                      <IL>True</IL> in order for the instance segmentation frame
                      to appear.
                    </p>
                  </Remark>
                </>
              }
            />
            <Attribute
              hashPrefix="event"
              name="instance_masks"
              type="Optional[dict[str, numpy.ndarray]]"
              description={
                <>
                  <p>
                    Segments every object instance in the scene, storing each
                    segmentation as a boolean image mask.
                  </p>
                  <p>
                    The keys are object IDs visible in the frame and each value
                    is the boolean numpy.ndarray of size
                    <IL>height</IL>, <IL>width</IL>) that segments the image.
                  </p>
                  {/* <VSpace s="15px" />
                <div
                  css={css`
                    width: 48%;
                    display: inline-block;
                  `}
                >
                  <Img fluid={data.instanceMask0.childImageSharp.fluid} />
                </div>
                <div
                  css={css`
                    width: 48%;
                    margin-left: 4%;
                    display: inline-block;
                  `}
                >
                  <Img fluid={data.instanceMask1.childImageSharp.fluid} />
                </div> */}
                  <Remark backgroundColor={color.dark.green5}>
                    <p>
                      Upon <IL>initialization</IL> or <IL>reset</IL>,{" "}
                      <IL>renderInstanceSegmentation</IL> must be set to{" "}
                      <IL>True</IL> in order for the instance masks to appear.
                    </p>
                  </Remark>
                </>
              }
            />
            <Attribute
              hashPrefix="event"
              name="instance_detections2D"
              type="Optional[dict[str, numpy.array]]"
              description={
                <>
                  <p>
                    Provides the 2D bounding box around every object instance in
                    the scene.
                  </p>
                  <p>
                    The keys are object IDs and the values are{" "}
                    <Latex>
                      [Upper Left $$x$$, Upper Left $$y$$, Lower Right $$x$$,
                      Lower Right $$y$$]
                    </Latex>
                    , where each element is the number of pixels it is from the
                    top left corner of the image.
                  </p>
                  <Remark backgroundColor={color.dark.green5}>
                    <p>
                      Upon <IL>initialization</IL> or <IL>reset</IL>,{" "}
                      <IL>renderInstanceSegmentation</IL> must be set to{" "}
                      <IL>True</IL> in order for the 2D instance detections to
                      appear.
                    </p>
                  </Remark>
                </>
              }
            />
            <Attribute
              hashPrefix="event"
              name="color_to_object_id"
              type="dict[tuple[int, int, int], str]"
              description={
                <p>
                  Provides a mapping from each pixel's color, represented as a
                  hashable tuple, in the <IL>instance_segmentation_frame</IL> to
                  what object it represents.
                </p>
              }
            />
            <Attribute
              hashPrefix="event"
              name="object_id_to_color"
              type="dict[str, tuple[int, int, int]]"
              description={
                <p>
                  Same as <IL>color_to_object_id</IL>, except for the keys and
                  values being swapped.
                </p>
              }
            />
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Metadata" emoji="exploding_head">
          <p>
            The metadata dictionary contains summary information about the state
            of the environment.
          </p>
          <SideBySideCode
            example={
              <>
                <Code lines={["event.metadata"]} />
                <Response
                  lines={[
                    "{",
                    `    "errorMessage": {...}`,
                    `    "lastActionSuccess": {...}`,
                    `    "actionReturn": {...}`,
                    `    "lastAction": {...}`,
                    "",
                    `    "sceneName": {...}`,
                    `    "sceneBounds": {...}`,
                    "",
                    `    "arm": {...}`,
                    `    "agent": {...}`,
                    `    "objects": {...}`,
                    "",
                    `    "fov": {...}`,
                    `    "screenWidth": {...}`,
                    `    "screenHeight": {...}`,
                    // "",
                    // '    "inventoryObjects": {...},',
                    `    {...}`,
                    "}",
                  ]}
                />
              </>
            }
            argsTitle="Metadata Response"
          >
            <Attribute
              hashPrefix="metadata"
              name="errorMessage"
              type="str"
              description={
                <p>
                  If an action fails, feedback is provided as to what caused the
                  failure.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="lastActionSuccess"
              type="bool"
              description={
                <>
                  <p>
                    States whether the last action was able to successfully
                    execute.
                  </p>
                  <p>
                    An exception will not be raised upon invalid states. For
                    instance, if an agent is standing right in-front of a wall
                    and tries to <IL>MoveAhead</IL>, it will fail, but an
                    exception will not be raised.
                  </p>
                  <p>
                    If an action is unsuccessful, the state of the environment
                    will not have changed.
                  </p>
                </>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="actionReturn"
              type="any"
              description={
                <p>
                  Holds the resulting data for actions that query the
                  environment, such as <IL>Get Reachable Positions</IL>.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="lastAction"
              type="str"
              description={
                <p>The name of the action passed into the Controller.</p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="sceneName"
              type="str"
              description={
                <p>
                  The name of the scene that the agent is currently located in.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="sceneBounds"
              type="dict[str, any]"
              description={
                <>
                  <p>
                    This object provides all coordinates that are within bounds
                    of the scene. This can be used in tandem with actions like
                    PlaceObjectAtPoint to make sure the coordinate used is not
                    out of bounds. This returns a sceneBounds object that
                    includes an 8x3 matrix of xyz coordinates that represent the
                    8 corners of the box encompassing the entire scene, an xyz
                    dictionary for the coordinates of the center of that box,
                    and an xyz dictionary for the size (extents) of that box.
                    Example:
                  </p>
                  <Code>
                    {String.raw`{
  "center": {
    'x': -1.45,
    'y': 1.407,
    'z': 0.2
  },
 "cornerPoints": [
   [ 1.5,   2.92,  3.2],
   [ 1.5,   2.92, -2.8],
   [ 1.5, -0.106,  3.2],
   [ 1.5, -0.106, -2.8],
   [-4.4,   2.92,  3.2],
   [-4.4,   2.92, -2.8],
   [-4.4, -0.106,  3.2],
   [-4.4, -0.106, -2.8]
  ],
 "size": {
   "x": 5.9,
   "y": 3.02,
   "z": 6.0
  }
}`}
                  </Code>
                </>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="arm"
              type="dict[str, any]"
              description={
                <p>
                  ManipulaTHOR specific information about the state of the
                  agent's arm. See{" "}
                  <Link to="#sub-arm-metadata">Arm Metadata</Link> for more.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="agent"
              type="dict[str, any]"
              description={
                <p>
                  Information about the pose of the agent. See{" "}
                  <Link to="#sub-agent-metadata">Agent Metadata</Link> for more.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="objects"
              type="list[dict[str, any]]"
              description={
                <p>
                  Information about the state of each object in the scene. See{" "}
                  <Link to="#sub-object-metadata">Object Metadata</Link> for
                  more.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="fov"
              type="float"
              description={
                <p>
                  The field of view of the agent's camera, which can be changed
                  upon initialization or reset.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="screenWidth"
              type="int"
              description={
                <p>
                  The number of pixels that make up the width of each rendered
                  image frame from the agent.
                </p>
              }
            />
            <Attribute
              hashPrefix="metadata"
              name="screenHeight"
              type="int"
              description={
                <p>
                  The number of pixels that make up the height of each rendered
                  image frame from the agent.
                </p>
              }
            />
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Arm Metadata" emoji="mechanical_arm">
          <p>Metadata specific to the arm ManipulaTHOR agent.</p>
          <SideBySideCode
            example={
              <>
                <Code lines={['event.metadata["arm"]']} />
                <Response
                  lines={[
                    "{",
                    '    "heldObjects": {...},',
                    '    "pickupableObjects": {...},',
                    "",
                    '    "handSphereCenter": {...},',
                    '    "handSphereRadius": {...},',
                    "",
                    '    "joints": {...},',
                    "}",
                  ]}
                />
              </>
            }
            argsTitle="Arm Metadata Response"
          >
            <Attribute
              name="heldObjects"
              type="list[str]"
              hashPrefix="arm"
              description={
                <p>A list of objectId's currently held by the agent.</p>
              }
            />
            <Attribute
              name="pickupableObjects"
              type="list[str]"
              hashPrefix="arm"
              description={
                <p>
                  All pickupable objects located within the agent's hand sphere
                  radius, which could be picked up.
                </p>
              }
            />
            <Attribute
              name="handSphereCenter"
              type="dict[str, float]"
              hashPrefix="arm"
              description={
                <p>
                  <Latex>
                    The global, world coordinate of the center $$(x, y, z)$$
                    position of the agent's hand sphere.
                  </Latex>
                </p>
              }
            />
            <Attribute
              name="handSphereRadius"
              type="float"
              hashPrefix="arm"
              description={
                <p>
                  The radius of the hand sphere, which determines how close
                  objects must be to allow them to be picked up. The value can
                  be changed with the{" "}
                  <Link to="#sub-set-hand-sphere-radius">
                    Set Hand Sphere Radius
                  </Link>
                  action.
                </p>
              }
            />
            <Attribute
              name="joints"
              type="list[dict[str, any]]"
              hashPrefix="arm"
              description={
                <p>
                  <Latex>
                    Metadata for each of the $$4$$ joints on the arm.
                  </Latex>{" "}
                  See the <Link to="#sub-joint-metadata">Joint Metadata</Link>{" "}
                  section for more.
                </p>
              }
            />
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Joint Metadata" emoji="bone">
          <p>
            <Latex>
              Metadata for each of the $$4$$ joints of the ManipulaTHOR arm.
            </Latex>
          </p>
          <SideBySideCode
            example={
              <>
                <Code
                  lines={[
                    "import random",
                    "i = random.choice(range(4))",
                    "",
                    'event.metadata["arm"]["joints"][i]',
                  ]}
                />
                <Response
                  lines={[
                    "{",
                    '    "name": {...},',
                    "",
                    '    "position": {...},',
                    '    "rootRelativePosition": {...},',
                    "",
                    '    "rotation": {...},',
                    '    "rootRelativeRotation": {...},',
                    '    "localRotation": {...},',
                    "}",
                  ]}
                />
              </>
            }
            argsTitle="Joint Metadata Response"
          >
            <Attribute
              name="name"
              type="str"
              hashPrefix="joint"
              description={
                <p>
                  The name of the joint. Names are in the form{" "}
                  <IL>robot_arm_i_jnt</IL> for{" "}
                  <Latex>{String.raw`$$i\in\{1, 2, 3, 4\}$$`}</Latex>.
                </p>
              }
            />
            <Attribute
              name="position"
              type="dict[str, float]"
              hashPrefix="joint"
              description={
                <p>
                  <Latex>
                    The global, world $$(x, y, z)$$ position of the joint.
                  </Latex>
                </p>
              }
            />
            <Attribute
              name="rootRelativePosition"
              type="dict[str, float]"
              hashPrefix="joint"
              description={
                <p>
                  <Latex>
                    The $$(x, y, z)$$ position of the joint, relative to the
                  </Latex>{" "}
                  <IL>armBase</IL>.
                </p>
              }
            />
            <Attribute
              name="rotation"
              type="dict[str, float]"
              hashPrefix="joint"
              description={
                <p>
                  <Latex>
                    The global, world $$(x, y, z, w)$$ rotation quaternion of
                    the joint.
                  </Latex>
                </p>
              }
            />
            <Attribute
              name="rootRelativeRotation"
              type="dict[str, float]"
              hashPrefix="joint"
              description={
                <p>
                  <Latex>
                    The $$(x, y, z, w)$$ rotation quaternion of this joint,
                    relative to the first joint of the arm.
                  </Latex>
                </p>
              }
            />
            <Attribute
              name="localRotation"
              type="dict[str, float]"
              hashPrefix="joint"
              description={
                <p>
                  <Latex>
                    The $$(x, y, z, w)$$ rotation quaternion of this joint,
                    relative to the previous joint of the arm, which is the
                    joint's parent in the hierarchy.
                  </Latex>
                </p>
              }
            />
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Agent Metadata" emoji="robot_face">
          <p>
            Within the metadata dictionary, the agent's key contains the pose of
            the agent after the action has executed.
          </p>
          <SideBySideCode
            example={
              <>
                <Code lines={['event.metadata["agent"]']} />
                <Response
                  lines={[
                    "{",
                    '    "cameraHorizon": {...},',
                    '    "isStanding": {...},',
                    '    "position": {...},',
                    '    "rotation": {...},',
                    "    {...}",
                    "}",
                  ]}
                />
              </>
            }
            argsTitle="Agent Metadata Response"
          >
            <Attribute
              hashPrefix="agent"
              name="cameraHorizon"
              type="float"
              description={
                <>
                  <p>
                    The angle in degrees that the camera's pitch is rotated.
                  </p>
                  <Warning>
                    <p>
                      As illustrated in the figure below, negative camera{" "}
                      <IL>horizon</IL> values correspond to agent looking up,
                      whereas positive <IL>horizon</IL> values correspond to the
                      agent looking down.
                    </p>
                  </Warning>
                  {/* <VSpace s="15px" />
                <Img fluid={data.horizon.childImageSharp.fluid} /> */}
                </>
              }
            />
            <Attribute
              hashPrefix="agent"
              name="isStanding"
              type="bool"
              description={
                <>
                  <p>
                    True if the agent is currently in a standing position,
                    otherwise false. This bool can be changed if the agent uses
                    the Stand or Crouch actions, which will change the camera
                    height.
                  </p>
                  <Warning>
                    <p>
                      The default agent is currently the only agent with the
                      ability to stand.
                    </p>
                  </Warning>
                </>
              }
            />
            <Attribute
              hashPrefix="agent"
              name="position"
              type="dict[str, float]"
              description={
                <>
                  <p>
                    <Latex>
                      The global position of the agent, with keys for $$x$$,
                      $$y$$, and $$z$$.
                    </Latex>
                  </p>
                  <p>
                    <Latex>
                      Note that $$y$$ corresponds to the upward coordinate in 3D
                      space.
                    </Latex>
                  </p>
                  {/* <VSpace s="15px" />
                <Img fluid={data.dimensions.childImageSharp.fluid} /> */}
                </>
              }
            />
            <Attribute
              hashPrefix="agent"
              name="rotation"
              type="dict[str, float]"
              description={
                <p>
                  <Latex>
                    The local rotation of the agent's body, with keys for $$x$$
                    (pitch), $$y$$ (yaw), and $$z$$ (roll). Since the LoCoBot's
                    body can only change its yaw rotation, both $$x$$ and $$z$$
                    will always be approximately 0.
                  </Latex>
                </p>
              }
            />
          </SideBySideCode>
        </SubSection>
        <SubSection sectionTitle="Object Metadata" emoji="door">
          <p>
            Each object has a plethora of information exposed about it during
            each step.
          </p>
          <SideBySideCode
            example={
              <>
                <Code lines={['event.metadata["objects"]']} />
                <Response
                  lines={[
                    "{",
                    '    "objectId": {...},',
                    '    "objectType": {...},',
                    '    "name": {...},',
                    "",
                    '    "distance": {...},',
                    '    "visible": {...},',
                    "",
                    '    "position": {...},',
                    '    "rotation": {...},',
                    "",
                    '    "axisAlignedBoundingBox": {...},',
                    '    "objectOrientedBoundingBox": {...},',
                    "",
                    '    "mass": {...},',
                    '    "salientMaterials": {...},',
                    "",
                    '    "parentReceptacles": {...},',
                    '    "receptacle": {...},',
                    '    "receptacleObjectIds": {...},',
                    "",
                    '    "ObjectTemperature": {...},',
                    '    "canChangeTempToHot": {...},',
                    '    "canChangeTempToCold": {...},',
                    "",
                    '    "breakable": {...},',
                    '    "isBroken": {...},',
                    "",
                    '    "canBeUsedUp": {...},',
                    '    "isUsedUp": {...},',
                    "",
                    '    "openable": {...},',
                    '    "isOpen": {...},',
                    '    "openness": {...},',
                    "",
                    '    "moveable": {...},',
                    '    "isMoving": {...},',
                    "",
                    '    "pickupable": {...},',
                    '    "isPickedUp": {...},',
                    "",
                    '    "sliceable": {...},',
                    '    "isSliced": {...},',
                    "",
                    '    "toggleable": {...},',
                    '    "isToggled": {...},',
                    "",
                    '    "canFillWithLiquid": {...},',
                    '    "isFilledWithLiquid": {...},',
                    "",
                    '    "cookable": {...},',
                    '    "isCooked": {...},',
                    "",
                    '    "dirtyable": {...},',
                    '    "isDirty": {...},',
                    // TODO: add temperatures
                    "}",
                  ]}
                />
              </>
            }
            argsTitle="Object Metadata Response"
          >
            <Attribute
              hashPrefix="object-metadata"
              name="objectId"
              type="str"
              description={
                <>
                  <p>
                    The unique ID of each object in the scene. It is generated
                    at runtime and composed of an object's <IL>objectType</IL>{" "}
                    and <IL>position</IL>.
                  </p>
                  <p>
                    Example: <IL>AlarmClock|-02.08|+00.94|-03.62</IL>.
                  </p>
                  {/* <Warning>
                    The <IL>objectId</IL> for each object may change upon
                    calling <Link to="#reset">reset</Link>, due to floating
                    point errors in the position. However, after the scene has
                    reset, each <IL>objectId</IL> will remain unchanged, even if
                    it's position slightly moves.
                    <VSpace s="15px" />
                    Thus, we recommend always fetching <IL>objectId</IL>'s from
                    the metadata, instead of storing them as constant string
                    literals in the code.
                  </Warning> */}
                </>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="objectType"
              type="str"
              description={
                <p>
                  The annotated type of the object. Each time is specified in
                  the <IL>Object Types</IL> section.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="name"
              type="str"
              description={
                <p>
                  Name of the object in Unity Scene. These names are unique
                  within any individual scene.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="distance"
              type="float"
              description={
                <p>
                  The Euclidean distance from near the center-point of the
                  object to the agent's camera.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="visible"
              type="bool"
              description={
                <>
                  <p>
                    Indicates whether the object is visible, and within the
                    initialized visibility distance of the agent.
                  </p>
                  <Warning>
                    <p>
                      The visible property does not mean the object is literally
                      visible in the frame. Rather, the object both has to be
                      visible and at a distance of less than the initialized{" "}
                      <IL>visibilityDistance</IL> away.
                    </p>
                  </Warning>
                </>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="position"
              type="dict[str, float]"
              description={
                <>
                  <p>
                    <Latex>
                      The global position of the object, with keys for $$x$$,
                      $$y$$, and $$z$$.
                    </Latex>
                  </p>
                  <p>
                    <Latex>
                      Note that $$y$$ corresponds to the upward coordinate in 3D
                      space.
                    </Latex>
                  </p>
                  {/* <VSpace s="15px" /> */}
                  {/* <Img fluid={data.dimensions.childImageSharp.fluid} /> */}
                </>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="rotation"
              type="dict[str, float]"
              description={
                <p>
                  <Latex>
                    The local rotation of the object, with keys for $$x$$
                    (pitch), $$y$$ (yaw), and $$z$$ (roll).
                  </Latex>
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="axisAlignedBoundingBox"
              type="dict[str, union[str, any]]"
              description={
                <>
                  <p>
                    Returns an axisAlignedBoundingBox object that includes an
                    8x3 matrix of xyz coordinates that represent the 8 corners
                    of the box, an xyz dictionary for the coordinates of the
                    center of the box, and an xyz dictionary for the size
                    (extents) of the box. This axis aligned bounding box is the
                    smallest box that can completely encloses the sim object
                    that is aligned to the world axis. This means that if the
                    object is rotated or moved, the overall size and volume of
                    this axis aligned box will change in order to remain aligned
                    relative to the static, world-axis. This is best used for
                    rough approximations of the area/volume an object takes up.
                    Do note that large, oddly shaped objects like countertops
                    that wrap around kitchens may have awkwardly defined
                    axis-aligned bounds. Example:
                  </p>
                  <Code
                    lines={[
                      "{",
                      '  "center": {',
                      '    "x": -1.336,',
                      '    "y":  1.098,',
                      '    "z":  0.221',
                      "  },",
                      ' "cornerPoints": [',
                      "   [-1.232, 1.277, 0.319],",
                      "   [-1.232, 1.277, 0.124],",
                      "   [-1.232, 0.919, 0.319],",
                      "   [-1.232, 0.919, 0.124],",
                      "   [ -1.44, 1.277, 0.319],",
                      "   [ -1.44, 1.277, 0.124],",
                      "   [ -1.44, 0.919, 0.319],",
                      "   [ -1.44, 0.919, 0.124],",
                      " ],",
                      ' "size": {',
                      '   "x": 0.208,',
                      '   "y": 0.358,',
                      '   "z": 0.195',
                      "  }",
                      "}",
                    ]}
                  />
                  <br />
                </>
              }
            />
            {/* TODO: why only return corner points */}
            <Attribute
              hashPrefix="object-metadata"
              name="objectOrientedBoundingBox"
              type="optional[dict[str, list[list[float]]]]"
              description={
                <>
                  <p>
                    Returns an objectOrientedBoundingBox object that includes an
                    8x3 matrix of xyz coordinates that represent the 8 corners
                    of the object oriented box. This object oriented bounding
                    box is a box that completely encloses a sim object. The
                    difference between this object oriented box and the axis
                    aligned box above is this box’s dimensions are static
                    relative to the object’s rotation and position. This means
                    this object oriented box will always have the same total
                    volume regardless of how the object is
                    manipulated/rotated/moved. Note that only Pickupable objects
                    have an objectOrientedboundingBox at this time. Example:
                  </p>
                  <Code>
                    {String.raw`{
  'cornerPoints': [
    [-1.445, 0.910, 0.115],
    [-1.228, 0.910, 0.115],
    [-1.228, 0.910, 0.328],
    [-1.445, 0.910, 0.328],
    [-1.445, 1.284, 0.115],
    [-1.228, 1.284, 0.115],
    [-1.228, 1.284, 0.328],
    [-1.445, 1.284, 0.328]
  ]
}`}
                  </Code>
                </>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="mass"
              type="float"
              description={
                <p>
                  The mass of a Pickupable or Moveable sim object in Kilograms
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="salientMaterials"
              type="list[str]"
              description={
                <p>
                  Array of strings listing the salient materials a pickupable
                  object is composed of. Valid strings are: Metal, Wood,
                  Plastic, Glass, Ceramic, Stone, Fabric, Rubber, Food, Paper,
                  Wax, Soap, Sponge, and Organic.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="parentReceptacles"
              type="list[str]"
              description={
                <p>
                  A list of objectId strings of all receptacles that contain
                  this object.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="receptacle"
              type="bool"
              description={
                <p>
                  If True, this object has the Receptacle property and can
                  contain other objects.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="receptacleObjectIds"
              type="list[str]"
              description={
                <p>
                  If the object is a receptacle, this is an array of objectIds
                  that the receptacle contains.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="ObjectTemperature"
              type="str"
              description={
                <p>
                  String that indicates what this object’s current abstracted
                  temperature is. Valid strings are: Hot, Cold, RoomTemp.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="canChangeTempToHot"
              type="bool"
              description={
                <p>
                  If True, this object is a source of Heat and can contextually
                  change other object’s Temperature to Hot.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="canChangeTempToCold"
              type="bool"
              description={
                <p>
                  If True, this object is a source of Cold and can contextually
                  change other object’s Temperature to Cold.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="breakable"
              type="bool"
              description={
                <p>
                  If True, this object has the Breakable property and can be
                  broken with the BreakObject action or contextually if enough
                  force is applied to it.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isBroken"
              type="bool"
              description={
                <p>
                  Only used if this object is breakable = True. If True, this
                  object is currently in its broken state. If False, this object
                  is not broken.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="canBeUsedUp"
              type="bool"
              description={
                <p>
                  If True, this object has the UsedUp property and can change
                  its state to a depleted form with the UsedUpObject action.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isUsedUp"
              type="bool"
              description={
                <p>
                  Only used if this object is canBeUsedUp = True. If True, this
                  object is currently in its used up state. If False, the object
                  is full and not used up yet.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="openable"
              type="bool"
              description={
                <p>
                  If True, this object has the openable property and can be
                  switched to an open or closed state with the OpenObject and
                  CloseObject actions.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isOpen"
              type="bool"
              description={
                <p>
                  Only used if this object is openable = True. If True, this
                  object is currently in its opened state. If False, this object
                  is in its closed state.
                </p>
              }
            />
            {/* bug on default value */}
            <Attribute
              hashPrefix="object-metadata"
              name="openness"
              type="float"
              description={
                <p>
                  The proportion that the object is open, linearly scaled to the
                  range <Latex>$$[0:1]$$</Latex>.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="moveable"
              type="bool"
              description={
                <p>
                  If True, this object has the Moveable property and can be
                  moved around the environment with actions like PushObject.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isMoving"
              type="bool"
              description={
                <p>
                  A bool tracking if this object is actively in motion. This can
                  be useful when tracking object behaviors while using
                  PausePhysicsAutoSim and AdvancePhysicsStep actions.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="pickupable"
              type="bool"
              description={
                <p>
                  If True, this object has the Pickupable property and can be
                  picked up by the agent with the PickupObject action.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isPickedUp"
              type="bool"
              description={
                <p>
                  Only used if this object is pickupable = True. If True, this
                  object is currently being picked up by the agent. If False,
                  the object is not being held by the agent.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="sliceable"
              type="bool"
              description={
                <p>
                  If True, this object has the sliceable property and can be
                  sliced into multiple pieces with the SliceObject action.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isSliced"
              type="bool"
              description={
                <p>
                  Only used if this object is sliceable = True. If True, this
                  object has been sliced. If False, this object is full and not
                  sliced.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="toggleable"
              type="bool"
              description={
                <p>
                  If True, this object has the Toggleable property and can be
                  manipulated with the ToggleObjectOn and ToggleObjectOff
                  actions.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isToggled"
              type="bool"
              description={
                <p>
                  Only used if this object is Toggleable = True. If True, this
                  object is toggled on. If False, the object is toggled off.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="canFillWithLiquid"
              type="bool"
              description={
                <p>
                  If True, this object has the Fillable property and can be
                  filled with liquid using the FillObjectWithLiquid action or
                  via contextual interactions with some parts of the
                  environment.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isFilledWithLiquid"
              type="bool"
              description={
                <p>
                  Only used if this object is canFillWithLiquid = True. If True,
                  this object is currently filled. If False, this object is
                  empty.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="cookable"
              type="bool"
              description={
                <p>
                  If True, this object has the cookable property and can change
                  its state to a cooked form with the CookObject action.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isCooked"
              type="bool"
              description={
                <p>
                  Only used if this object is cookable = True. If True, this
                  object is currently in its cooked state. If False, the object
                  is not cooked.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="dirtyable"
              type="bool"
              description={
                <p>
                  If True, this object has the Dirty property, allowing it to be
                  switched between clean and dirty states using the DirtyObject
                  and CleanObject actions.
                </p>
              }
            />
            <Attribute
              hashPrefix="object-metadata"
              name="isDirty"
              type="bool"
              description={
                <p>
                  Only used if this object is dirtyable = True. If True, this
                  object is currently in its dirty state. If False, this object
                  is in its clean state.
                </p>
              }
            />
          </SideBySideCode>
        </SubSection>
      </Section>
    </BlogWrapper>
  );
}

export const query = graphql`
  fragment FluidImage on File {
    childImageSharp {
      fluid(quality: 100) {
        ...GatsbyImageSharpFluid
      }
    }
  }

  query {
    moveArm: file(relativePath: { eq: "manipulathor/MoveArm.png" }) {
      ...FluidImage
    }
    moveArmBase: file(relativePath: { eq: "manipulathor/MoveBaseArm.png" }) {
      ...FluidImage
    }
    pickupObject: file(relativePath: { eq: "manipulathor/PickUpObject.png" }) {
      ...FluidImage
    }
    releaseObject: file(
      relativePath: { eq: "manipulathor/ReleaseObject.png" }
    ) {
      ...FluidImage
    }
    restrictMovement: file(
      relativePath: { eq: "manipulathor/RestrictMovement.png" }
    ) {
      ...FluidImage
    }
  }
`;
